import React from "react";
import { MessageKey, fill, t } from ".";

/**
 * A sentence with bold text, a link or a line break inside it, kept whole so
 * each language can put the pieces in its own order:
 *
 *   en.json: "Reset link sent to <0>{email}</0>."
 *   <T k="forgotPassword.sent" vars={{ email }} tags={{ 0: (c) => <strong>{c}</strong> }} />
 *
 * Each <n>…</n> calls tags[n] with that text; <n/> renders tags[n] as is
 * (an element, e.g. <br />).
 * Tags are read from the translation before values are filled in, so a name
 * that happens to contain "<0>" is shown as text, never as markup.
 */
type Tag = React.ReactElement | ((children: string) => React.ReactNode);

export function T({ k, vars, tags }: { k: MessageKey; vars?: Record<string, string | number>; tags: Record<string, Tag> }) {
  const template = t(k, typeof vars?.count === "number" ? { count: vars.count } : undefined);
  const out: React.ReactNode[] = [];
  const re = /<(\w+)\/>|<(\w+)>([\s\S]*?)<\/\2>/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(template))) {
    if (m.index > last) out.push(fill(template.slice(last, m.index), vars));
    const tag = tags[m[1] ?? m[2]];
    const content = fill(m[3] ?? "", vars);
    if (!tag) out.push(fill(m[0], vars));
    else out.push(<React.Fragment key={out.length}>{typeof tag === "function" ? tag(content) : tag}</React.Fragment>);
    last = re.lastIndex;
  }
  if (last < template.length) out.push(fill(template.slice(last), vars));
  return <>{out}</>;
}
